import {useQuery} from "@tanstack/react-query"
import {get, isEmpty} from "lodash"
import {EMPTY_STUDENT_STATISTICS} from "helpers"
import {majorService, studentService} from "services"
import {Auth} from "types/auth"

type UseStudentOverallStatsParams = {
    studentProfileId?: number
    currentMajorId?: number
    currentMajorVersionId?: number | Auth.CurrentMajorVersion
}

const getMajorVersionId = (currentMajorVersionId?: number | Auth.CurrentMajorVersion) => {
    if (typeof currentMajorVersionId === "number") {
        return currentMajorVersionId
    }
    return currentMajorVersionId?.id
}

export const useStudentOverallStats = ({
    studentProfileId,
    currentMajorId,
    currentMajorVersionId
}: UseStudentOverallStatsParams) => {
    const majorVersionId = getMajorVersionId(currentMajorVersionId)

    const statsQuery = useQuery(
        ["student-overall-stats", studentProfileId],
        () => studentService.getOverallStats({studentProfileId}),
        {
            enabled: !!studentProfileId,
            staleTime: 60 * 1000 // 1 minute
        }
    )

    const programHoursQuery = useQuery(
        ["program-version-clock-hours", currentMajorId, majorVersionId],
        async () => {
            const major = await majorService.getMajor(currentMajorId)
            if (isEmpty(major) || isEmpty(major.versions)) {
                return 0
            }
            const currentVersion = major.versions.find((version) => version.id === majorVersionId)
            return get(currentVersion, "totalClockHours", 0)
        },
        {
            enabled: !!currentMajorId && !!majorVersionId,
            staleTime: 30 * 60 * 1000 // 30 minutes
        }
    )

    return {
        studentStats: statsQuery.data ?? EMPTY_STUDENT_STATISTICS,
        programVersionTotalClockHours: programHoursQuery.data ?? 0,
        isLoading: statsQuery.isLoading
    }
}
